import type CurrencyCode from '@alltrails/types/CurrencyCode';
import { getPurchaseEventId, getSignupEventId, getTrialStartEventId } from './conversionsApi';

const getCurrencyPrecision = currencyCode => {
  // eslint-disable-next-line @alltrails/custom/prefer-react-intl -- reading a currency's fraction-digit precision via resolvedOptions(); no locale formatting and no intl context available in this util
  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currencyCode
  });

  // The 'maximumFractionDigits' property contains the precision
  return formatter.resolvedOptions().maximumFractionDigits ?? 2;
};

// https://developers.openai.com/ads/supported-events#event-data-shapes
// Send monetary values as integers in the currency’s lowest denomination, for example 12999 for $129.99 with currency: "USD".
export const getAmountFromPrice = (price: number, currencyCode: CurrencyCode) => {
  const precision = getCurrencyPrecision(currencyCode);
  return Math.round(price * Math.pow(10, precision));
};

// Check for presence of oaiq in case of content blockers
const isOpenAiPixelUnavailable = () => {
  return typeof oaiq === 'undefined';
};

// https://developers.openai.com/ads/measurement-pixel#lead-generation-and-registration
export const trackCompleteRegistrationForOpenAi = (userId: number) => {
  if (isOpenAiPixelUnavailable()) {
    return;
  }

  oaiq(
    'measure',
    'registration_completed',
    {
      type: 'customer_action'
    },
    {
      event_id: getSignupEventId(userId)
    }
  );
};

// https://developers.openai.com/ads/measurement-pixel#subscription-and-trial-events
export const trackPurchaseForOpenAi = ({
  currencyCode,
  value,
  isTrial,
  recurlyPlanCode,
  subscriptionEventId
}: {
  currencyCode: CurrencyCode;
  value: number;
  isTrial: boolean;
  recurlyPlanCode?: string;
  subscriptionEventId?: number;
}) => {
  if (isOpenAiPixelUnavailable() || !subscriptionEventId) {
    return;
  }

  if (isTrial) {
    oaiq(
      'measure',
      'trial_started',
      {
        type: 'plan_enrollment',
        plan_id: recurlyPlanCode
      },
      {
        event_id: getTrialStartEventId(subscriptionEventId)
      }
    );
  } else {
    oaiq(
      'measure',
      'subscription_created',
      {
        type: 'plan_enrollment',
        plan_id: recurlyPlanCode,
        amount: getAmountFromPrice(value, currencyCode),
        currency: currencyCode
      },
      {
        event_id: getPurchaseEventId(subscriptionEventId)
      }
    );
  }
};
